/**
 * Logistics deliveries.
 *
 * A thin, paginated view over the existing delivery list, filtered by the
 * things a dispatcher actually asks: which location, what status, and whether a
 * row came from a sheet or happened here. Paginated by the API, because the
 * ledger only grows.
 */

import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DELIVERY_ORIGIN_LABELS, type DeliveryOrigin } from '@cenox/shared';
import {
  Badge, Card, EmptyState, ErrorState, Pagination, Select, TableSkeleton,
} from '../../components/ui';
import { api } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { money } from '../../lib/format';
import { useLocationFilter, useLocationReady } from '../../lib/locationContext';
import { GeoScopeFields } from '../../components/GeoScope';

interface DeliveryRow {
  id: string;
  externalRef: string;
  riderName: string | null;
  destination: string;
  fee: string;
  riderPayout: string;
  deliveryStatus: string | null;
  paymentStatus: string | null;
  originSource: string;
  recordOrigin?: string;
  postedAt: string | null;
}

export function LogisticsDeliveriesPage(): ReactNode {
  const { business } = useAuth();
  const path = useBusinessPath();
  const currency = business?.currencyCode ?? 'NGN';

  // Country, state and location: the sidebar's shared scope.
  const scope = useLocationFilter();
  const locationReady = useLocationReady();
  const [status, setStatus] = useState('');
  const [origin, setOrigin] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 25;


  const query = useQuery({
    queryKey: ['logistics', 'deliveries', path(''), { scope, status, origin, page }],
    queryFn: () => {
      const search = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      for (const [k, v] of Object.entries(scope)) if (v) search.set(k, v);
      if (status) search.set('deliveryStatus', status);
      /*
        Two axes, two parameters.

        "Imported" is not a channel — it says the record came from a
        spreadsheet, and a delivery that arrived that way still came from
        WhatsApp or the shop. This control sent it as `originSource`, which the
        server rejects outright, so selecting Imported returned an error and the
        569 deliveries it was built to show were unreachable.
      */
      if (origin === 'import') search.set('recordOrigin', 'import');
      else if (origin) search.set('originSource', origin);
      return api.get<{ data: DeliveryRow[]; total?: number }>(
        path(`/deliveries?${search.toString()}`),
      );
    },
    staleTime: 30_000,
    enabled: locationReady,
  });

  if (query.isError) {
    return <ErrorState message="Could not load deliveries." onRetry={() => void query.refetch()} />;
  }

  const rows = query.data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-2.5">
        <GeoScopeFields bare idPrefix="logistics-scope" />
        <Select
          label="Status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          options={[
            { value: '', label: 'All statuses' },
            { value: 'Pending', label: 'Pending' },
            { value: 'Assigned', label: 'Assigned' },
            { value: 'In Transit', label: 'In transit' },
            { value: 'Delivered', label: 'Delivered' },
            { value: 'Completed', label: 'Completed' },
            { value: 'Cancelled', label: 'Cancelled' },
            { value: 'Failed/Returned', label: 'Failed or returned' },
          ]}
        />
        <Select
          label="Source"
          value={origin}
          onChange={(e) => {
            setOrigin(e.target.value);
            setPage(1);
          }}
          options={[
            { value: '', label: 'All sources' },
            { value: 'whatsapp', label: 'WhatsApp' },
            { value: 'customer_app', label: 'App' },
            { value: 'manual', label: 'Manual' },
            { value: 'shop', label: 'Shop' },
            /*
              The absence of a channel is still a thing to filter on.

              569 imported deliveries carry no recognisable source, and leaving
              them off this list made them the one group you could see in the
              table and not ask for — which is how a third of the book becomes
              invisible to the control built to find it.
            */
            { value: 'unknown', label: 'Not recorded' },
            // The other axis: how the record arrived, not where the job did.
            { value: 'import', label: 'Imported' },
          ]}
        />
      </div>

      {query.isPending ? (
        <TableSkeleton />
      ) : rows.length === 0 ? (
        <EmptyState title="No deliveries match these filters" />
      ) : (
        <>
          <Card>
            <div className="-mx-3 overflow-x-auto px-3">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="py-2 pr-3 font-medium">Reference</th>
                    <th className="py-2 pr-3 font-medium">Rider</th>
                    <th className="py-2 pr-3 font-medium">Destination</th>
                    <th className="py-2 pr-3 text-right font-medium">Fee</th>
                    <th className="py-2 pr-3 text-right font-medium">Payout</th>
                    <th className="py-2 pr-3 font-medium">Status</th>
                    <th className="py-2 font-medium">Source</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((d) => (
                    <tr key={d.id}>
                      <td className="py-2 pr-3">
                        <Link
                          to={`/deliveries/${d.id}`}
                          className="font-medium text-brand-700 hover:underline"
                        >
                          {d.externalRef}
                        </Link>
                      </td>
                      <td className="py-2 pr-3 text-slate-700">{d.riderName ?? '—'}</td>
                      <td className="py-2 pr-3 text-slate-600">{d.destination || '—'}</td>
                      <td className="py-2 pr-3 text-right tabular-nums text-slate-900">
                        {money(d.fee, currency)}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums text-slate-700">
                        {money(d.riderPayout, currency)}
                      </td>
                      <td className="py-2 pr-3">
                        <Badge
                          tone={
                            d.deliveryStatus === 'Delivered' || d.deliveryStatus === 'Completed'
                              ? 'success'
                              : d.deliveryStatus === 'Cancelled' ||
                                  d.deliveryStatus === 'Failed/Returned'
                                ? 'danger'
                                : 'warning'
                          }
                        >
                          {d.deliveryStatus ?? '—'}
                        </Badge>
                      </td>
                      <td className="py-2 text-xs text-slate-500">
                        {DELIVERY_ORIGIN_LABELS[d.originSource as DeliveryOrigin] ??
                          d.originSource}
                        {d.recordOrigin === 'import' ? (
                          <span className="ml-1 text-slate-400">· imported</span>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Pagination
            page={page}
            pageSize={pageSize}
            total={query.data?.total ?? rows.length}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}
